/**
 * *Agree price…* on a delivery group whose charge is not yet priced (order to
 * invoice doc 104 §5.6, §9.5): staff agree the delivery price ex VAT and say
 * why; its goods can then be released and despatched. Posts to the charge's
 * `agree_price_url` with its `line_refs`. Nothing is sent until there is a
 * price and a reason to send; what is wrong with them (and how long a reason
 * may be) is the server's refusal, bound to the field it names. `onAgree`
 * resolves to the refusal to show, or null once done.
 */
import { useId, useState } from "react";
import type {
  AgreeDeliveryPriceRequest,
  DeliveryGroupView,
} from "@shared-core/data/order-to-invoice-types";
import { penceOfPounds } from "@shared-core/domain/delivery-selection";
import type { OrderStepFailure } from "@shared-core/domain/order-to-invoice-presentation";
import { Button } from "../../primitives/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../primitives/dialog";
import { Input } from "../../primitives/input";
import { FailureBanner, Field } from "./OrderDialogs";

export function AgreeDeliveryPriceDialog({
  group,
  busy,
  onClose,
  onAgree,
}: {
  group: DeliveryGroupView;
  busy: boolean;
  onClose: () => void;
  onAgree: (
    input: AgreeDeliveryPriceRequest,
  ) => Promise<OrderStepFailure | null>;
}) {
  const id = useId();
  const [price, setPrice] = useState("");
  const [reason, setReason] = useState("");
  const [failure, setFailure] = useState<OrderStepFailure | null>(null);
  const pence = penceOfPounds(price);
  const errorOf = (field: "price_pence" | "reason") =>
    failure?.fieldErrors[field];
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Agree the delivery price</DialogTitle>
          <DialogDescription>
            Delivery to {group.destination_name} is not yet priced. Its goods
            are released and despatched once it is.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={async (e) => {
            e.preventDefault();
            if (pence === null || !reason.trim()) return;
            // The delivery is its pending lines' refs, comma-separated (the value the dry run's choice carries).
            // Staff using this dialog are the ones the customer agreed the price with.
            const refused = await onAgree({
              delivery: (group.charge?.line_refs ?? []).join(","),
              price_pence: pence,
              reason: reason.trim(),
              confirmed: ["customer_agreed"],
            });
            setFailure(refused);
            if (!refused) onClose();
          }}
        >
          <FailureBanner failure={failure} />
          <Field
            id={`${id}-price`}
            label="Agreed price"
            error={errorOf("price_pence")}
          >
            {(aria) => (
              <Input
                {...aria}
                inputMode="decimal"
                placeholder="£"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                className="tabular-nums"
              />
            )}
          </Field>
          <Field id={`${id}-reason`} label="Reason" error={errorOf("reason")}>
            {(aria) => (
              <Input
                {...aria}
                value={reason}
                placeholder="Why this price"
                onChange={(e) => setReason(e.target.value)}
              />
            )}
          </Field>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Back
            </Button>
            <Button
              type="submit"
              disabled={busy || pence === null || !reason.trim()}
            >
              Agree price
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
